// Career Miniapp — API fetch wrapper
const apiBase = () => {
    if (typeof window !== 'undefined' && window.__CAREER_CONFIG__) {
        return window.__CAREER_CONFIG__.site.api_base || '/api/jobs';
    }
    return '/api/jobs';
};
const lang = () => {
    if (typeof window !== 'undefined' && window.__CAREER_CONFIG__) {
        return window.__CAREER_CONFIG__.site.lang || 'hu';
    }
    return 'hu';
};
// [F-CANDTRACK] strukturált API-hiba: a hívó a status alapján dönthet (pl. 404 →
// "hirdetés nem található" nézet) — string-matching helyett
export class ApiError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}
async function fetchJson(url, options) {
    const res = await fetch(url, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
        ...options,
    });
    if (!res.ok) {
        throw new ApiError(`API error ${res.status} on ${url}`, res.status);
    }
    return res.json();
}
async function postForm(url, formData) {
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData,
    });
    if (!res.ok) {
        throw new ApiError(`API error ${res.status} on ${url}`, res.status);
    }
    return res.json();
}
export const api = {
    siteConfig(langCode) {
        const l = langCode || lang();
        return fetchJson(`${apiBase()}/site-config?lang=${encodeURIComponent(l)}`);
    },
    page(key, langCode) {
        const l = langCode || lang();
        return fetchJson(`${apiBase()}/page?lang=${encodeURIComponent(l)}&key=${encodeURIComponent(key)}`);
    },
    job(slug, referralId, preview) {
        // M-3: opcionális ?referral_id= query → backend visszaad referral mezőt
        // [CU-869eh36qh] opcionális ?preview= token → inaktív hirdetés előnézete
        const params = new URLSearchParams();
        // [WILLIAM-sup-2224] a felület aktuális nyelve (SSR: ?lang= / cookie / a hirdetés nyelve): a backend
        // ezen adja a szekció-címeket, a jelentkezési űrlap mező-feliratait és a lista-értékeket - nélküle a
        // hirdetés saját nyelve döntött, és az EN-re váltott oldalon a magyar feliratok maradtak
        params.set('lang', lang());
        if (referralId)
            params.set('referral_id', referralId);
        if (preview)
            params.set('preview', preview);
        const qs = params.toString() ? `?${params.toString()}` : '';
        return fetchJson(`${apiBase()}/${encodeURIComponent(slug)}${qs}`);
    },
    // M-3: új endpoint a job-detail mount-kor (view counter)
    // Body: {job_id, utm, referral_id, hrfy}
    trackJobView(jobId, utm, referralId, hrfy) {
        const fd = new FormData();
        fd.append('job_id', String(jobId));
        if (utm)
            fd.append('utm', utm);
        if (referralId)
            fd.append('referral_id', referralId);
        // hrfy.hu rövidlinkes érkezés — a backend ViewLogger a rövidlink-domainre könyveli a nézetet
        if (hrfy === '1')
            fd.append('hrfy', '1');
        return postForm(`${apiBase()}/track-view`, fd);
    },
    search(params) {
        const qs = new URLSearchParams();
        qs.set('current_lang', params.current_lang || lang());
        if (params.offset != null)
            qs.set('offset', String(params.offset));
        if (params.limit != null)
            qs.set('limit', String(params.limit));
        Object.keys(params).forEach(k => {
            if (['current_lang', 'offset', 'limit'].indexOf(k) === -1 && params[k] != null && params[k] !== '') {
                qs.set(k, String(params[k]));
            }
        });
        return fetchJson(`${apiBase()}/search?${qs.toString()}`);
    },
    defaultForm(langCode) {
        const l = langCode || lang();
        return fetchJson(`${apiBase()}/defaultform?language=${encodeURIComponent(l)}`);
    },
    apply(formData) {
        return postForm(`${apiBase()}/apply`, formData);
    },
    // [F-CANDTRACK] Jelentkezés-követő timeline. A token POST body-ban megy
    // (query-ben TILOS: az access/proxy logokba kerülne).
    applicationStatus(jobId, token) {
        const fd = new FormData();
        fd.append('job_id', String(jobId));
        fd.append('token', token);
        return postForm(`${apiBase()}/application-status`, fd);
    },
    // GET /api/jobs/related/{slug} — pontozott (tag+kategória+lokáció) kapcsolódó állások
    relatedJobs(slug) {
        return fetchJson(`${apiBase()}/related/${encodeURIComponent(slug)}`);
    },
    // [F-WILLFORM F2] szinkron CV-kinyerés a jelentkezés közben (cv_only/cv_form/chat)
    interviewExtract(jobId, cvFile, sessionUid, sessionSecret) {
        const fd = new FormData();
        fd.append('job_id', String(jobId));
        fd.append('privacy_accept', 'true');
        fd.append('cv', cvFile);
        if (sessionUid)
            fd.append('interview_session_uid', sessionUid);
        if (sessionSecret)
            fd.append('interview_session_secret', sessionSecret);
        return postForm(`${apiBase()}/interview/extract`, fd);
    },
    // [F-WILLFORM F5] LinkedIn profil-import a jelentkezéshez
    interviewLinkedin(jobId, url, sessionUid, sessionSecret) {
        const fd = new FormData();
        fd.append('job_id', String(jobId));
        fd.append('privacy_accept', 'true');
        fd.append('url', url);
        if (sessionUid)
            fd.append('interview_session_uid', sessionUid);
        if (sessionSecret)
            fd.append('interview_session_secret', sessionSecret);
        return postForm(`${apiBase()}/interview/linkedin`, fd);
    },
    // [F-WILLFORM F3] chat-jelentkezés indítása
    interviewStart(jobId, sessionUid, sessionSecret) {
        const fd = new FormData();
        fd.append('job_id', String(jobId));
        fd.append('privacy_accept', 'true');
        if (sessionUid)
            fd.append('interview_session_uid', sessionUid);
        if (sessionSecret)
            fd.append('interview_session_secret', sessionSecret);
        return postForm(`${apiBase()}/interview/start`, fd);
    },
    // [F-WILLFORM F3] chat-üzenet (válasz / kihagyás / javítás / gomb-katt)
    interviewMessage(jobId, sessionUid, sessionSecret, payload) {
        const fd = new FormData();
        fd.append('job_id', String(jobId));
        fd.append('interview_session_uid', sessionUid);
        fd.append('interview_session_secret', sessionSecret);
        if (payload.text !== undefined)
            fd.append('text', payload.text);
        if (payload.skip)
            fd.append('skip', 'true');
        if (payload.redo)
            fd.append('redo', 'true');
        // [F-CHATOPT] gyorsválasz-gomb: opció-id-k JSON-tömbként (szerver-oldali allowlist)
        if (payload.optionIds && payload.optionIds.length > 0) {
            fd.append('option_ids', JSON.stringify(payload.optionIds));
        }
        // [F-CHATOPT] stale/dupla kérés őr: a kliens szerinti aktuális kérdés-kulcs
        if (payload.expectedKey)
            fd.append('expected_key', payload.expectedKey);
        return postForm(`${apiBase()}/interview/message`, fd);
    },
};
